import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CircularProgress, Box, Typography, Alert } from '@mui/material';
import { workflowService } from '../services/api/workflowService';
import { useToast } from '../context/ToastContext';

/**
 * Redirect component for handling old workflow URL formats
 * 
 * This component takes external IDs in URLs and redirects to the new
 * URL format using database IDs for consistency
 */
const WorkflowRedirect: React.FC = () => {
  const { clientCode, externalId } = useParams<{ clientCode: string; externalId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Determine which module the user is trying to access
  const currentPath = window.location.pathname;
  const moduleMatch = currentPath.match(/\/module(\d+)\//);
  const moduleNumber = moduleMatch ? parseInt(moduleMatch[1], 10) : 1; // Default to module1 if not found
  
  useEffect(() => {
    const redirectToDbIds = async () => {
      try {
        if (!clientCode || !externalId) {
          throw new Error('Missing client code or external ID in URL');
        }
        
        console.log(`Redirecting from external ID URL: module=${moduleNumber}, client=${clientCode}, externalId=${externalId}`);
        
        // First, check if this external ID already has a mapping in localStorage
        const cachedDbId = localStorage.getItem(`external_id_${externalId}`);
        if (cachedDbId) {
          console.log(`Found cached mapping: external ID ${externalId} -> DB ID ${cachedDbId}`);
          
          // Use the clientCode directly for the redirect in this case
          navigate(`/module${moduleNumber}/client/${clientCode}/${cachedDbId}`, { replace: true });
          showToast('Using cached workflow mapping', 'info');
          return;
        }
        
        // Try to get client info from database
        const clients = await workflowService.getDbClients();
        console.log(`Retrieved ${clients.length} clients from database`);
        
        // First try exact match on client_code
        let client = clients.find(c => c.client_code === clientCode);
        
        // If no exact match, try case-insensitive match
        if (!client) {
          client = clients.find(c => 
            c.client_code.toLowerCase() === clientCode.toLowerCase()
          );
        }
        
        // If still no match but clientCode is numeric, try matching by client_id
        if (!client && /^\d+$/.test(clientCode)) {
          const clientId = parseInt(clientCode, 10);
          client = clients.find(c => c.client_id === clientId);
        }
        
        // Final fallback - if client not found in database, still proceed with the externalId lookup
        // We'll use the clientCode as-is for the redirect
        if (!client) {
          console.warn(`Client with code ${clientCode} not found in database, proceeding with external ID lookup only`);
        }
        
        // Next, get the workflow database ID from the external ID
        try {
          const workflow = await workflowService.getDbWorkflowByExternal(externalId);
          
          if (!workflow) {
            throw new Error(`Workflow with external ID ${externalId} not found in database`);
          }
          
          // We have the workflow database ID, prepare for redirect
          const effectiveClientId = client ? client.client_id : clientCode;
          console.log(`Redirecting to database IDs: client=${effectiveClientId}, workflow=${workflow.workflow_id}`);
          
          // Store the mapping in localStorage for reference
          localStorage.setItem(`external_id_${externalId}`, workflow.workflow_id.toString());
          localStorage.setItem(`workflow_${workflow.workflow_id}_external_id`, externalId);
          
          // Redirect to the new URL format with the appropriate module
          navigate(`/module${moduleNumber}/client/${effectiveClientId}/${workflow.workflow_id}`, { replace: true });
          
          // Show a toast notification about the URL change
          showToast('URL format has been updated to use database IDs', 'info');
        } catch (workflowError) {
          console.error('Error fetching workflow by external ID:', workflowError);
          
          // Try direct navigation with the external ID - this might work if the backend
          // can handle external IDs directly in the workflow route
          console.log(`Attempting direct navigation with external ID: /module${moduleNumber}/client/${clientCode}/${externalId}`);
          navigate(`/module${moduleNumber}/client/${clientCode}/${externalId}`, { replace: true });
          
          // Don't show an error toast in this case as we're trying a fallback approach
          showToast('Redirecting using original ID format', 'info');
        }
      } catch (error) {
        console.error('Error during URL redirect:', error);
        setError(error instanceof Error ? error.message : 'Error redirecting to workflow');
        showToast('Error redirecting to workflow', 'error');
        setIsLoading(false);
      }
    };
    
    redirectToDbIds();
  }, [clientCode, externalId, moduleNumber, navigate, showToast]);
  
  if (error) {
    return (
      <Box sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">Redirect Error</Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
          <button 
            onClick={() => navigate('/client-selection')}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            Go to Client Selection
          </button>
          <button 
            onClick={() => navigate('/')}
            className="px-4 py-2 border border-blue-500 text-blue-500 rounded-md hover:bg-blue-50"
          >
            Return to Dashboard
          </button>
        </Box>
      </Box>
    );
  }
  
  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center', 
      justifyContent: 'center', 
      height: '100vh' 
    }}>
      <CircularProgress size={40} />
      <Typography variant="h6" sx={{ mt: 2 }}>
        Redirecting...
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Updating to new URL format with database IDs
      </Typography>
    </Box>
  );
};

export default WorkflowRedirect;